import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { FAQList } from '../../../commodity/components/ui/FAQItem';
import { RootState } from '../../../store/store';
import { colors } from '../../../theme';
import content from '../content/teacher-landing';

/**
 * S2.1 (TCH-01) — the teacher acquisition landing page.
 *
 * The Wave A audit found no teacher front door at all: a candidate who heard
 * about IQMaxx arrived at a page selling classes to parents, and the only way
 * into the teacher journey was to guess that "Registrarse" hides a role
 * selector.
 *
 * Two rules shape this screen, both from the story's acceptance criteria:
 *
 *  - **Nothing here routes through the guardian journey** (TCH-01-FR-05). The
 *    call to action lands where the candidate actually is: signup pre-set to
 *    TEACHER if they have no account, the application itself if they do.
 *  - **Media never blocks the application** (TCH-01-FR-03 / DoD). The
 *    introductory video is optional in every sense — absent, blocked or broken,
 *    the page still reads and the CTA is still reachable.
 *
 * All copy lives in `content/teacher-landing.ts`; this file decides only where
 * a candidate goes next.
 */

/** Where "apply" leads, given who is asking. */
export function applyTarget(isAuthenticated: boolean, role?: string): string {
  if (isAuthenticated && role === 'TEACHER') return '/teacher-onboarding';
  // Everyone else — signed out, or signed in under a learner/guardian account —
  // starts a teacher account. Sending a parent to /teacher-onboarding would
  // bounce off the role guard, which reads as the product being broken.
  return '/register?role=TEACHER';
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="h4"
    component="h2"
    sx={{
      fontWeight: 800,
      color: colors.primary.main,
      mb: 3,
      fontSize: { xs: '1.5rem', md: '2rem' },
    }}
  >
    {children}
  </Typography>
);

/**
 * The introductory video, or an honest stand-in for it.
 *
 * `<video>` failures are silent by default: a missing file renders an empty
 * black rectangle and the visitor concludes the site is broken. This renders
 * the fallback on `error` instead, and never mounts a player at all when no URL
 * is configured for the environment.
 */
const IntroVideo: React.FC = () => {
  const [failed, setFailed] = useState(false);
  const { url, title, caption, fallbackBody } = content.video;

  const showPlayer = Boolean(url) && !failed;

  return (
    <Box>
      <SectionTitle>{title}</SectionTitle>
      {showPlayer ? (
        <Box
          component="video"
          data-testid="teacher-intro-video"
          controls
          preload="metadata"
          src={url as string}
          onError={() => setFailed(true)}
          sx={{
            width: '100%',
            maxWidth: 720,
            borderRadius: 3,
            bgcolor: '#000',
            display: 'block',
          }}
        />
      ) : (
        <Box
          data-testid="teacher-intro-video-fallback"
          sx={{
            maxWidth: 720,
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            bgcolor: colors.primary.lightest,
            border: `1px solid ${colors.primary.light}`,
          }}
        >
          <Typography variant="body1" sx={{ color: colors.text.secondary, lineHeight: 1.8 }}>
            {fallbackBody}
          </Typography>
        </Box>
      )}
      <Typography variant="body2" sx={{ color: colors.text.secondary, mt: 1.5 }}>
        {caption}
      </Typography>
    </Box>
  );
};

const TeacherLanding: React.FC = () => {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const isTeacher = isAuthenticated && user?.role === 'TEACHER';
  const target = applyTarget(Boolean(isAuthenticated), user?.role);
  const ctaLabel = isTeacher ? content.hero.ctaLabelSignedIn : content.hero.ctaLabel;

  const ctaSx = {
    bgcolor: colors.primary.main,
    color: '#fff',
    fontWeight: 700,
    textTransform: 'none' as const,
    borderRadius: 999,
    px: 4,
    py: 1.4,
    fontSize: '1rem',
    '&:hover': { bgcolor: colors.primary.dark },
  };

  return (
    <Box>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: colors.primary.lightest, py: { xs: 6, md: 9 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{ color: colors.primary.dark, fontWeight: 700, letterSpacing: 1.5 }}
              >
                {content.hero.eyebrow}
              </Typography>
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 900,
                  color: colors.primary.main,
                  lineHeight: 1.15,
                  my: 1.5,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                }}
              >
                {content.hero.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: colors.text.secondary, mb: 3.5, lineHeight: 1.8, maxWidth: 640 }}
              >
                {content.hero.lead}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                <Button component={Link} to={target} sx={ctaSx}>
                  {ctaLabel}
                </Button>
                {!isAuthenticated && (
                  <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                    {content.hero.signInPrompt}{' '}
                    <Link to="/login" style={{ color: colors.primary.dark, fontWeight: 700 }}>
                      Inicia sesión
                    </Link>
                  </Typography>
                )}
              </Box>

              {isAuthenticated && !isTeacher && (
                <Typography variant="body2" sx={{ color: colors.text.secondary, mt: 2 }}>
                  Tu sesión actual no es de profesor. Enseñar requiere una cuenta de profesor
                  aparte.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                aria-hidden
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: { xs: 180, md: 300 },
                  fontSize: { xs: '5rem', md: '7rem' },
                  userSelect: 'none',
                }}
              >
                👩‍🏫
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Why teach here ───────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: 'white', py: { xs: 5, md: 8 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {content.valueProps.map((prop) => (
              <Grid item xs={12} sm={6} md={3} key={prop.title}>
                <Box
                  sx={{
                    height: '100%',
                    p: 3,
                    borderRadius: 3,
                    border: `1px solid ${colors.primary.lightest}`,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
                  }}
                >
                  <Box sx={{ fontSize: '2rem', mb: 1 }} aria-hidden>
                    {prop.emoji}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, fontSize: '1.05rem' }}>
                    {prop.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.text.secondary, lineHeight: 1.7 }}
                  >
                    {prop.body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Introductory video ───────────────────────────────────────────── */}
      <Box sx={{ bgcolor: colors.primary.lightest, py: { xs: 5, md: 8 } }}>
        <Container maxWidth="lg">
          <IntroVideo />
        </Container>
      </Box>

      {/* ── About working with us ────────────────────────────────────────── */}
      <Box sx={{ bgcolor: 'white', py: { xs: 5, md: 8 } }}>
        <Container maxWidth="lg">
          <SectionTitle>{content.about.title}</SectionTitle>
          <Grid container spacing={3}>
            {content.about.blocks.map((block) => (
              <Grid item xs={12} sm={6} md={3} key={block.step}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: colors.primary.main,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    mb: 1.5,
                  }}
                >
                  {block.step}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, fontSize: '1.05rem' }}>
                  {block.title}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.text.secondary, lineHeight: 1.7 }}>
                  {block.body}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Testimonial ──────────────────────────────────────────────────── */}
      {content.testimonial && (
        <Box sx={{ bgcolor: colors.secondary.lightest, py: { xs: 5, md: 7 } }}>
          <Container maxWidth="md">
            <Typography
              component="blockquote"
              sx={{
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                fontStyle: 'italic',
                lineHeight: 1.8,
                color: colors.text.primary,
                m: 0,
              }}
            >
              “{content.testimonial.quote}”
            </Typography>
            <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 800 }}>
              {content.testimonial.name}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              {content.testimonial.detail}
            </Typography>
          </Container>
        </Box>
      )}

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: 'white', py: { xs: 5, md: 8 } }}>
        <Container maxWidth="md">
          <SectionTitle>{content.faq.title}</SectionTitle>
          <FAQList items={content.faq.entries} />
        </Container>
      </Box>

      {/* ── Closing call to action ───────────────────────────────────────── */}
      <Box sx={{ bgcolor: colors.primary.main, py: { xs: 5, md: 7 } }}>
        <Container maxWidth="md">
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 800,
              color: '#fff',
              mb: 1.5,
              fontSize: { xs: '1.35rem', md: '1.7rem' },
            }}
          >
            {content.closing.title}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'rgba(255,255,255,0.9)', mb: 3, lineHeight: 1.8 }}
          >
            {content.closing.body}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Button
              component={Link}
              to={target}
              sx={{
                bgcolor: '#fff',
                color: colors.primary.dark,
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 999,
                px: 4,
                py: 1.4,
                '&:hover': { bgcolor: colors.primary.lightest },
              }}
            >
              {isTeacher ? content.hero.ctaLabelSignedIn : content.closing.ctaLabel}
            </Button>
            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
              <Link to="/help" style={{ color: '#fff', fontSize: '0.875rem' }}>
                Centro de ayuda
              </Link>
              <Link to="/terms" style={{ color: '#fff', fontSize: '0.875rem' }}>
                Términos del Servicio
              </Link>
              <Link to="/privacy" style={{ color: '#fff', fontSize: '0.875rem' }}>
                Aviso de Privacidad
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default TeacherLanding;
